import { unwrap } from "@nexxt/api-client";
import type { FollowClient } from "@nexxt/app-kit";
import { attributeTaskStageLabel, linesheetStatusLabel } from "@nexxt/catalog-ui";
import type { MovementStatus } from "@nexxt/contracts/logistics";
import type { FollowableType } from "@nexxt/contracts/notifications";
import { vendorReturnStatusLabel } from "@nexxt/logistics-ui";
import { inboundStatusLabel, poStatusLabel } from "@nexxt/procurement-ui";
import { api } from "./api";
import { followedLink } from "./bell-links";

// The followed objects on their gateway (ADR-281): the three routes, the page of each object from `bell-links.ts`,
// and its status in the words of the badges.

const { follows } = api.notifications;
const one = follows[":entityType"][":entityId"];
const inWords = (labels: Record<string, () => string>) => (status: string) => labels[status]?.();
/**
 * The words of each status, as the badges of the app say them; the status as it is for an unknown one. Orders,
 * claims and shipments are Tomorrow's alone to follow.
 */
const statusLabels: Partial<Record<FollowableType, (status: string) => string | undefined>> = {
  "procurement.purchase_order": inWords(poStatusLabel),
  "procurement.inbound": inWords(inboundStatusLabel),
  "catalog.linesheet": inWords(linesheetStatusLabel),
  "catalog.attribute_task": inWords(attributeTaskStageLabel),
  // a vendor return in the supplier's words (ADR-169 point 6): its own turn
  "logistics.stock_movement": (status) => vendorReturnStatusLabel(status as MovementStatus, false),
};

const param = ({ entityType, entityId }: { entityType: string; entityId: string }) => ({
  param: { entityType: entityType as FollowableType, entityId },
});

export const followClient: FollowClient = {
  list: () => unwrap(follows.$get()),
  follow: (target, level) => unwrap(one.$put({ ...param(target), json: { level } })),
  unfollow: (target) => unwrap(one.$delete(param(target))),
  linkOf: followedLink,
  statusOf: ({ entityType, status }) => statusLabels[entityType as FollowableType]?.(status) ?? status,
};
